<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <Toolbar class="p-mb-4 p-p-2 page-toolbar">
          <template #start>
            <div style="font-size: 1.4em; color: inherit" class="p-p-2">{{ $t('Email Settings') }}</div>
            <div class="p-p-1" v-show="canManageAndDelete">
              <AddNewButton :disabled="dataTableIsLoading"
                            :loading="dataTableIsLoading" :label="$t('Add')"
                            @click="addNewItem"/>
            </div>
          </template>
        </Toolbar>

        <Spinner v-if="isLoading"/>
        <DesktopDataTable v-else
                          :showHeader="false"
                          :dataTableIsLoading="dataTableIsLoading"
                          :itemsArrayIsEmpty="itemsArrayIsEmpty"
                          :sort="sort"
                          :showSearchInput="false"
                          :actionButtonsCount="actionButtonsCount"
                          @sort-items="sortItems"
                          :headers="[
              {name: 'username', title: $t('Email'), sortable: false},
              {name: 'sender_name', title: $t('Sender Name'), sortable: false},
              {name: 'auto_receive_emails', title: $t('autoReceiveEmailsLabel'), width: '15%', hint: $t('autoReceiveEmailsHint'), style: {'white-space': 'normal'}, sortable: false},
              {name: 'imap_mark_as_seen', title: $t('markEmailsAsSeenLabel'), width: '15%', hint: $t('markEmailsAsSeenHint'), style: {'white-space': 'normal'}, sortable: false},
              {name: 'imap_fetch_unseen_only', title: $t('fetchUnseenEmailsOnlyLabel'), width: '15%', hint: $t('fetchUnseenEmailsOnlyHint'), style: {'white-space': 'normal'}, sortable: false},
              {name: 'access', title: $t('Users'), sortable: false},
              {name: 'created_at', title: $t('Created'), sortable: true},
          ]">
          <template v-slot:body>
            <tr v-for="(item, index) of items"
                :key="index"
                :class="{'updated-item': isItemUpdated(item.id)}">
              <td>
                <span v-if="item.username">{{ item.username }}</span>
                <i v-else class="ti-minus"/>
              </td>
              <td>
                <span v-if="item.sender_name">{{ item.sender_name }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <i v-if="item.auto_receive_emails && item.incomingMailAvailable"
                   class="ti-check"/>
                <i v-else-if="item.auto_receive_emails"
                   class="ti-lock"
                   v-tooltip.top="$t('billing.features.incoming_mail.pausedHint')"/>
                <i v-else class="ti-close"/>
              </td>

              <!--              <td>-->
              <!--                <i class="ti-check" v-if="item.imap_mark_as_seen"></i>-->
              <!--                <i class="ti-close" v-else/>-->
              <!--              </td>-->
              <td>
                <i v-if="item.imap_mark_as_seen && item.incomingMailAvailable" class="ti-check"/>
                <i v-else-if="item.imap_mark_as_seen"
                   class="ti-lock"
                   v-tooltip.top="$t('billing.features.incoming_mail.pausedHint')"/>
                <i v-else class="ti-close"/>
              </td>

              <td>
                <i v-if="[true, 1, '1'].includes(item.imap_fetch_unseen_only) && item.incomingMailAvailable"
                   class="ti-check"/>
                <i v-else-if="[true, 1, '1'].includes(item.imap_fetch_unseen_only)"
                   class="ti-lock"
                   v-tooltip.top="$t('billing.features.incoming_mail.pausedHint')"/>
                <i v-else class="ti-close"/>
              </td>

              <td>
                <div v-if="item.emailUsers?.length" class="p-badge"
                     v-tooltip.top="createTooltipData(item)">{{ item.emailUsers.length }}
                </div>
                <div v-else class="p-badge">0</div>
              </td>

              <td class="table-date-line">
                <span v-if="item.created_at">
                  {{ formatBy(item.created_at, item.creator) }}
                </span>
                <i v-else class="ti-minus"/>
              </td>

              <td v-show="actionButtonsCount">
                <div class="actions">
                 <span class="p-d-inline-flex"
                       v-tooltip.left="billingActionTooltipOptions(
                           false,
                            $t('manageEmailAccountAccessAndSignaturesTooltip'))">
                    <Button :disabled="dataTableIsLoading || !allDataLoaded || billingDataEditingLocked"
                            icon="ti-user"
                            class="p-button-secondary p-button-raised"
                            @click="editEmailAccountUsersOnClick(item)"/>
                  </span>
                  <EditButton v-show="canManageAndDelete" @click="editItem(item)" :disabled="dataTableIsLoading"/>

                  <DeleteButton v-show="canManageAndDelete"
                                @click="confirmItemDelete(item)"
                                :disabled="dataTableIsLoading"/>
                </div>
              </td>
            </tr>
          </template>
        </DesktopDataTable>

        <OwnPaginator v-show="!itemsArrayIsEmpty"
                      :rows="pagination.perPage"
                      :currentPage="pagination.currentPage - 1"
                      :totalPages="pagination.pageCount"
                      :totalRecords="pagination.totalCount"
                      :rowsPerPageOptions="[5,10,25,50,100,200]"
                      :pageLinkSize="11"/>

        <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

        <EmailAccountModal :visible="itemModal"
                           :item="item"
                           :createInCurrentLocation="true"
                           @update-items="updateItems"
                           @close="closeItemModal"/>

        <EmailAccountUsersModal :visible="emailAccountUsersModal"
                                :item="emailAccount"
                                :employees="employees"
                                @update-items="updateItems"
                                @close="closeEmailAccountUsersModal"/>

        <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                            :modalDataIsLoading="modalDataIsLoading"
                            @close="closeConfirmItemDeleteModal"
                            @delete-item="deleteEmailAccount"
                            :data="itemToDeleteData"/>

      </div>

    </div>
  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import EmailAccountModal from "@/pages/account/components/EmailAccountModal";
import formatMixins from "@/mixins/formatMixins";
import datatableMixins from "@/mixins/datatableMixins";
import httpMixins from "@/mixins/httpMixins";
import settings from "@/settings";
import EmailAccountUsersModal from "@/pages/settings/components/EmailAccountUsersModal";
import billingRestrictionsMixin from '@/mixins/billingRestrictionsMixin'

export default {
  mixins: [ formatMixins, datatableMixins, httpMixins, billingRestrictionsMixin ],
  components: { EmailAccountModal, EmailAccountUsersModal },
  data() {
    return {
      getEmployeesInFlight: false,
      employeesLoaded: false,
      emailAccountUsersModal: false,
      emailAccount: {},
      employees: [],
      // fieldsExpand: 'emailUsers.userData,emailUsers.userData.effective_role,creator,canManageAccess,hasCurrentUserAccess',
      fieldsExpand: 'emailUsers.userData,emailUsers.userData.effective_role,creator,incomingMailAvailable',
      apiLink: 'email-account/get-by-location',
      deleteItemUrl: 'email-account/delete',
      routePath: '/email-settings',
      searchData: '',
      sort: {
        column: 'id',
        direction: '-',
      },
    }
  },
  watch: {
    '$store.state.firstLayerIsOpened'(value) {
      if (!value) {
        if (this.emailAccountUsersModal) {
          this.closeEmailAccountUsersModal()
        }
      }
    },
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.checkRouteQuery()
    this.getPageData()
  },
  methods: {
    editEmailAccountUsersOnClick(item) {
      if (this.billingActionIsRestricted()) return

      this.emailAccount = {...item}
      this.emailAccountUsersModal = true
      this.$store.commit('toggleFirstLayer', true)
    },
    closeEmailAccountUsersModal() {
      this.emailAccount = {}
      this.emailAccountUsersModal = false
      this.$store.commit('toggleFirstLayer', false)
    },
    getPageData() {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.getItems()
      this.getUsersForEmailAccounts(true)
    },
    async getUsersForEmailAccounts(getOnlyAdminsAndModerators = true) {
      if (!this.$store.getters.userCanGetData || this.getEmployeesInFlight) return

      this.getEmployeesInFlight = true
      this.employeesLoaded = false

      try {
        const { status, data } = await httpClient.post('user/get-users-for-email-account', {
          getOnlyAdminsAndModerators
        })

        if (status === 200 && Array.isArray(data)) {
          this.employees = data
          this.employeesLoaded = true
        } else {
          this.employees = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.getEmployeesInFlight = false
      }
    },
    createTooltipData(item) {
      if (!item.emailUsers?.length) {
        return false
      }
      const emailUsers = item.emailUsers
      let html = ''
      emailUsers.forEach(user => {
        html += `<div>${user.userData?.first_name ?? ''} ${user.userData?.last_name ?? ''}</div>`
      })

      return html
    },
    async getItems() {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('emailAccountsSettings')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.searchData,
            expand: this.fieldsExpand,
          },
          signal: ctrl?.signal
        })


        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        if (this._abortors?.emailAccountsSettings === ctrl) {
          this._abortors.emailAccountsSettings = null
        }
      }
    },
  },
  computed: {
    allDataLoaded() {
      return this.employeesLoaded
    },
    canManageAndDelete() {
      return this.$store.getters.isAdminOrHigher
    },
    actionButtonsCount() {
      return this.canManageAndDelete ? 3 : 0
    },
    itemToDeleteData() {
      return  this.itemToDelete?.username ?? null
    },
  }
}
</script>